import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { formatDate } from '../../utils/timeFormat';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  MapPin, Calendar, Clock, DollarSign, Users, Building2, AlertTriangle, CheckCircle
} from 'lucide-react';
import { formatTimeRange } from '../../utils/timeFormat';
import { toast } from 'sonner';

const AgencyJobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applying, setApplying] = useState(false);
  const [coverNote, setCoverNote] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [myApps, setMyApps] = useState({}); // job_id → active application
  const [withdrawingId, setWithdrawingId] = useState(null);
  const [filters, setFilters] = useState({
    city: '',
    state: '',
    role_id: ''
  });

  useEffect(() => {
    fetchJobs();
    fetchRoles();
    fetchMyApplications();
  }, []);

  const fetchMyApplications = async () => {
    try {
      const res = await api.get('/agency/public/my-applications');
      const map = {};
      (res.data || []).forEach((a) => {
        if (a.status !== 'withdrawn') map[a.job_id] = a;
      });
      setMyApps(map);
    } catch {
      // non-fatal — job list still renders
    }
  };

  const handleWithdraw = async (jobId, applicationId) => {
    if (!window.confirm('Withdraw your application for this job? You can reapply while it stays open.')) return;
    setWithdrawingId(applicationId);
    try {
      await api.post(`/agency/public/applications/${applicationId}/withdraw`);
      toast.success('Application withdrawn.');
      await fetchMyApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to withdraw application.');
    } finally {
      setWithdrawingId(null);
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.city) params.append('city', filters.city);
      if (filters.state) params.append('state', filters.state);
      if (filters.role_id) params.append('role_id', filters.role_id);
      
      const res = await api.get(`/agency/public/jobs?${params.toString()}`);
      setJobs(res.data);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await api.get('/roles');
      setRoles(res.data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const handleApply = async () => {
    if (!selectedJob) return;
    
    setApplying(true);
    try {
      await api.post(`/agency/public/jobs/${selectedJob.id}/apply`, {
        job_id: selectedJob.id,
        cover_note: coverNote
      });
      toast.success('Application submitted!');
      setShowConfirmation(true);
      setSelectedJob(null);
      setCoverNote('');
      fetchMyApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="agency-jobs-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Building2 className="w-8 h-8 text-cyan-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Agency Jobs</h1>
            <p className="text-gray-500 text-sm">Direct booking opportunities from agencies</p>
          </div>
        </div>

        {/* Direct Hire Notice */}
        <Alert className="mb-6 bg-amber-50 border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Important:</strong> These are direct-booking jobs. Payment will be made directly by the booking party, not through the iEvntür platform.
          </AlertDescription>
        </Alert>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Input
                placeholder="City"
                value={filters.city}
                onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                data-testid="filter-city"
              />
              <Input
                placeholder="State (e.g., CA)"
                value={filters.state}
                onChange={(e) => setFilters({ ...filters, state: e.target.value.toUpperCase() })}
                maxLength={2}
                data-testid="filter-state"
              />
              <Select 
                value={filters.role_id || "all"} 
                onValueChange={(v) => setFilters({ ...filters, role_id: v === "all" ? "" : v })}
              >
                <SelectTrigger data-testid="filter-role">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                onClick={fetchJobs} 
                className="bg-cyan-500 hover:bg-cyan-600"
                data-testid="search-btn"
              >
                Search
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Jobs List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        ) : jobs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No agency jobs available at the moment</p>
              <p className="text-sm text-gray-400 mt-1">Check back later or try different filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4" data-testid="jobs-list">
            {jobs.map((job) => (
              <Card 
                key={job.id} 
                className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-amber-400"
                onClick={() => setSelectedJob(job)}
                data-testid="agency-job-card"
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">{job.title}</h3>
                        <Badge className="bg-amber-100 text-amber-800">Direct Booking</Badge>
                        {myApps[job.id] && (
                          <Badge
                            className={myApps[job.id].status === 'accepted'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-cyan-100 text-cyan-800'}
                            data-testid={`agency-job-applied-${job.id}`}
                          >
                            {myApps[job.id].status === 'accepted' ? 'Hired' : 'Applied'}
                          </Badge>
                        )}
                        {job.spots_remaining <= 2 && (
                          <Badge className="bg-red-100 text-red-800">Only {job.spots_remaining} spots left</Badge>
                        )}
                      </div>
                      
                      <div className="space-y-1 text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          <span>{job.company_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{job.venue_name} • {job.city}, {job.state}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(job.job_date)}</span>
                          <Clock className="w-4 h-4 ml-2" />
                          <span>{formatTimeRange(job.start_time, job.end_time)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          <span>{job.spots_remaining} spots available</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-2xl font-bold text-cyan-600">${job.hourly_rate}/hr</div>
                      <div className="text-sm text-gray-500">Pay Rate</div>
                      {myApps[job.id]?.status === 'pending' && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={withdrawingId === myApps[job.id].id}
                          onClick={(e) => { e.stopPropagation(); handleWithdraw(job.id, myApps[job.id].id); }}
                          className="mt-2 border-rose-200 text-rose-700 hover:bg-rose-50"
                          data-testid={`agency-job-withdraw-${job.id}`}
                        >
                          {withdrawingId === myApps[job.id].id ? 'Withdrawing…' : 'Withdraw'}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Job Details & Apply Dialog */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="max-w-2xl">
          {selectedJob && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedJob.title}</DialogTitle>
                <DialogDescription>
                  {selectedJob.company_name} • {selectedJob.city}, {selectedJob.state}
                </DialogDescription>
              </DialogHeader>

              {/* Direct Hire Warning */}
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  <strong>Direct Booking Notice:</strong> {selectedJob.direct_hire_notice}
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                {/* Job Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-500 mb-1">Date</div>
                    <div className="font-medium">
                      {formatDate(selectedJob.job_date)}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-500 mb-1">Time</div>
                    <div className="font-medium">{formatTimeRange(selectedJob.start_time, selectedJob.end_time)}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-500 mb-1">Pay Rate</div>
                    <div className="font-medium text-cyan-600 text-xl">${selectedJob.hourly_rate}/hr</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-500 mb-1">Spots Available</div>
                    <div className="font-medium">{selectedJob.spots_remaining}</div>
                  </div>
                </div>

                {selectedJob.description && (
                  <div>
                    <h4 className="font-medium mb-2">Description</h4>
                    <p className="text-sm text-gray-600">{selectedJob.description}</p>
                  </div>
                )}

                {/* Cover Note */}
                <div>
                  <label className="text-sm font-medium">Cover Note (Optional)</label>
                  <Textarea
                    value={coverNote}
                    onChange={(e) => setCoverNote(e.target.value)}
                    placeholder="Introduce yourself and share why you're a great fit..."
                    rows={3}
                    className="mt-1"
                    data-testid="cover-note-input"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedJob(null)}>Cancel</Button>
                <Button 
                  onClick={handleApply}
                  disabled={applying || !!myApps[selectedJob.id]}
                  className="bg-cyan-500 hover:bg-cyan-600"
                  data-testid="apply-btn"
                >
                  {myApps[selectedJob.id] ? 'Already Applied' : applying ? 'Applying...' : 'Apply Now'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Success Confirmation */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent>
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <DialogTitle className="text-xl mb-2">Application Submitted!</DialogTitle>
            <DialogDescription className="mb-4">
              Your application has been sent to the hiring party. If accepted, they will contact you directly for next steps.
            </DialogDescription>
            <Alert className="bg-amber-50 border-amber-200 text-left">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-amber-700 text-sm">
                Remember: This is a direct booking job. If accepted, payment will be made directly by the booking party, not through iEvntür.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowConfirmation(false)} className="w-full">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgencyJobsPage;
